import { AioButtonComponent } from '@aio/ui';
import { Component, ChangeDetectionStrategy, computed, signal, inject, effect, OnInit } from '@angular/core';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { RouterModule, Router, ActivatedRoute } from '@angular/router';
import { AuthFooterComponent } from '../footer/auth-footer.component';
import { AuthService } from '../../../core/services/auth.service';
import { LoginCredentials, ApiError } from '../../../core/models/auth.models';
import { LoaderService } from '../../../core/services/loader.service';
import { PermissionService } from '../../../core/services/permission.service';
import { FormValidators } from '../../../shared/utils/form-validators';
import { DailyLandingService } from '../../../core/services/daily-landing.service';
import { pickRandomAuthHero } from '../auth-hero.util';
import { AuthHeroPanelComponent } from '../auth-hero-panel/auth-hero-panel.component';


@Component({
  selector: 'app-login',
  standalone: true,
  imports: [
    AioButtonComponent,CommonModule, ReactiveFormsModule, RouterModule, AuthFooterComponent, AuthHeroPanelComponent],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LoginComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly loaderService = inject(LoaderService);
  private readonly permissionService = inject(PermissionService);
  private readonly dailyLanding = inject(DailyLandingService);

  // Signals for reactive state management
  readonly loading = signal(false);
  readonly errorMessage = signal('');
  readonly successMessage = signal('');
  readonly loginAttempts = signal(0);
  readonly passwordVisible = signal(false);
  readonly formValid = signal(false);
  readonly submitted = signal(false);
  /** Tenant ID from the last successful login response (used for dashboard URL and navigation). */
  readonly loggedInTenantId = signal<string | null>(null);

  /** Random marketing hero; chosen once when this component is created. */
  readonly hero = signal(pickRandomAuthHero());

  // Reactive form with custom email validation
  readonly loginForm: FormGroup = this.fb.group({
    email: ['', [Validators.required, FormValidators.enhancedEmail()]],
    password: ['', [Validators.required]]
  });

  // Computed properties
  readonly loginLocked = computed(() => this.loginAttempts() >= 5);
  readonly canSubmit = computed(() => {
    return !this.loading() && !this.loginLocked();
  });
  readonly showFieldErrors = computed(() => this.submitted());

  constructor() {
    // Track form validity changes
    effect(() => {
      const isValid = this.loginForm.valid;
      this.formValid.set(isValid);
    });

    // Listen to form value changes
    this.loginForm.valueChanges.subscribe(() => {
      const isValid = this.loginForm.valid;
      this.formValid.set(isValid);
    });

    // Listen to form status changes
    this.loginForm.statusChanges.subscribe((status) => {
      this.formValid.set(status === 'VALID');
    });
  }

  ngOnInit(): void {
    // Check for success message from password reset
    this.route.queryParams.subscribe(params => {
      if (params['message']) {
        this.successMessage.set(params['message']);
        // Clear the message from URL
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: {},
          replaceUrl: true
        });
      }
    });
  }

  onSubmit(): void {
    // Mark as submitted to show validation errors
    this.submitted.set(true);

    // Check if form is valid before proceeding
    if (!this.loginForm.valid) {
      return;
    }

    if (!this.canSubmit()) {
      return;
    }

    const credentials: LoginCredentials = this.loginForm.value;

    this.loading.set(true);
    this.loaderService.setDisabled(true);
    this.errorMessage.set('');

    this.authService.login(credentials).subscribe({
      next: (response) => {
        this.loading.set(false);
        this.loaderService.setDisabled(false);
        if (response.success) {
          this.loginAttempts.set(0);

          // Check if MFA is required
          if (response.data?.mfaToken && !response.data?.loginToken) {
            // Store auth data for MFA flow
            localStorage.setItem('auth_data', JSON.stringify(response));
            // AuthService will handle the redirect to MFA
            return;
          }

          let tenantId = '';
          if (response.data?.shards?.length && response.data.shards[0].tenantId) {
            tenantId = response.data.shards[0].tenantId;
            this.loggedInTenantId.set(tenantId);
          }

          if (tenantId) {
            // Load user permissions before navigation to ensure they're available for AuthGuard
            // Force refresh to ensure we get the user ID from the permissions API
            this.permissionService.getUserPermissions(true, tenantId).subscribe({
              next: (permissions) => {

                // Navigate after permissions are loaded
                const path = this.dailyLanding.getPostAuthDestinationSegments(tenantId);
                const url = `/${path.join('/')}`;
                this.router.navigateByUrl(url).catch(err => {
                  console.error('Navigation error:', err);
                });
              },
              error: (error) => {
                console.error('Error loading user permissions:', error);
                // Still navigate even if permission loading fails
                // const path = [tenantId, 'leads'];
                // console.log('Navigating to path (despite permission error):', path);
                // this.router.navigate(path).catch(err => {
                //   console.error('Navigation error:', err);
                // });
              }
            });
          } else {
            console.error('No tenant ID available for navigation');
            this.errorMessage.set('Unable to determine tenant. Please contact your administrator.');
          }
        } else {
          this.handleLoginFailure(response.message);
        }
      },
      error: (error: ApiError) => {
        this.loading.set(false);
        this.loaderService.setDisabled(false);
        this.handleLoginFailure(error.message);
      }
    });
  }

  private handleLoginFailure(message?: string): void {
    this.loginAttempts.update(attempts => attempts + 1);
    if (this.loginLocked()) {
      this.errorMessage.set('Too many failed attempts. Please reset your password or contact your admin.');
    } else {
      this.errorMessage.set('Email Address and/or Password is incorrect. Please try again.');
    }
  }

  clearError(): void {
    this.errorMessage.set('');
    this.successMessage.set('');
  }

  /**
   * Get the dashboard URL for display purposes (uses tenantId from login response when available).
   */
  getDashboardUrl(): string {
    const tenantId = this.loggedInTenantId() ?? this.authService.getStoredTenantId() ?? '';
    if (!tenantId) return '';
    const path = this.dailyLanding.getPostAuthDestinationSegments(tenantId);
    return `/${path.join('/')}`;
  }

  /**
   * Navigate to dashboard with proper tenant ID handling (uses tenantId from login response when available).
   */
  goToDashboard(): void {
    const tenantId = this.loggedInTenantId() ?? this.authService.getStoredTenantId() ?? '';
    if (tenantId) {
      const path = this.dailyLanding.getPostAuthDestinationSegments(tenantId);
      const url = `/${path.join('/')}`;
      this.router.navigateByUrl(url).catch(err => {
        console.error('Navigation error:', err);
      });
    } else {
      this.errorMessage.set('Unable to determine tenant. Please contact your administrator.');
    }
  }

  logout(): void {
    this.authService.logout().subscribe();
  }
}
